import {
  Button,
  Divider,
  Form,
  Input,
  message,
  Modal,
  Tooltip,
  Typography,
} from "antd";
import { useEffect, useRef, useState } from "react";
import { Scrollbars } from "react-custom-scrollbars-2";
import Candidate from "redux/models/candidate";
import { displayDollar } from "utilities/tableUtility";
import BillingApproval from "redux/models/billingApproval";
import EmptyPlaceholder from "common/components/emptyPlaceholder/emptyPlaceholder";
import { convertIntoDashUSFormat } from "utilities/dateUtility";
import SubmitOneTimeJobBilling from "common/submitOneTimeBilling/submitOneTimeJobBilling";
import { useParams } from "react-router-dom";
import BillingJob from "redux/models/billingJobs";
import { selectConfigration } from "redux/slices/configrationSlice";
import { useDispatch, useSelector } from "react-redux";
import { isPermissionPresent } from "utilities/generalUtility";
import { selectUser } from "redux/slices/userSlice";
import K from "utilities/constants";
import { RefundIcon } from "../../common/icons/RefundIcon";
import { IconButton } from "../../common/components/iconButton/iconButton";
import { PaymentAcceptedIcon } from "../../common/icons/PaymentAcceptedIcon";
import { CreditIcon } from "../../common/icons/CreditIcon";

const { Title, Paragraph } = Typography;

export default function CandidateIssueCredits({ styles, candidateActivity }) {
  const [issuesListing, setIssuesListing] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [issueCreditModal, setIssueCreditModal] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const configrationSlice = useSelector(selectConfigration);
  const userRoles = useSelector(selectUser).roles;

  const canIssueCredit = isPermissionPresent(
    K.Permissions.SubmitCandidateOneTimeBilling,
    userRoles,
  );
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const { clientId, jobId } = useParams();
  const [issueCreditForm] = Form.useForm();
  const issueCreditRef = useRef(null);

  const issueJobCredit = async (values) => {
    setIsLoading(true);
    try {
      const body = {
        submittedBillId: issueCreditRef.current.id,
        issueCreditReason: values.reason,
      };
      const issuedCredit = await BillingApproval.issueJobCredit(body);

      setIssuesListing((prev) => {
        const updated = prev.map((item) =>
          item.id === issueCreditRef.current.id ? { ...item, active: 0 } : item,
        );
        return issuedCredit ? [...updated, issuedCredit] : updated;
      });

      setIsLoading(false);
      issueCreditRef.current = null;
      handleIssueCreditCancel();
      message.success("Issue Credited Successfully.");
    } catch (err) {
      console.error(err);
      setIsLoading(false);
    }
  };
  const getAllCurrencies = async () => {
    try {
      await dispatch(BillingJob.getCurrencies());
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        getAllCurrencies();
        const res = await Candidate.getCandidateBillings(candidateActivity.id);
        setIssuesListing([...res]);
      } catch (err) {
        console.error(err);
      }
    })();
  }, [candidateActivity, refresh]);

  const show = () => {
    setIsModalVisible(true);
  };
  const handleIssueCreditCancel = () => {
    issueCreditForm.resetFields();
    setIssueCreditModal(false);
  };

  return (
    <>
      <div className={`${styles.issueCredits} issue-credit-head`}>
        <div className={styles.clientAmount}>
          <span>Total Amount</span>
          <Title level={5}>
            {displayDollar(
              issuesListing
                .reduce((prev, curr) => prev + curr.dublicateAmount, 0)
                .toFixed(2),
            )}
          </Title>
        </div>
        <div className="issue-btn-wrap">
          {isPermissionPresent(
            K.Permissions.SubmitCandidateOneTimeBilling,
            userRoles,
          ) && (
            <Button
              type="primary"
              className={styles.resubmitBtn}
              onClick={show}
              disabled={candidateActivity.isConsiderable === 0}
            >
              Submit One Time Billing
            </Button>
          )}
        </div>
      </div>
      <Scrollbars style={{ height: 400 }}>
        {issuesListing.length ? (
          issuesListing.map((item) => (
            <div className={styles.invoiceWrap}>
              {item.active && !item.billingTypeName.includes("(credit)") ? (
                canIssueCredit && (
                  <IconButton
                    icon={
                      <PaymentAcceptedIcon
                        style={{ fontSize: 16 }}
                        className="icon-primary"
                      />
                    }
                    onClick={() => {
                      issueCreditRef.current = item;
                      setIssueCreditModal(true);
                    }}
                  />
                )
              ) : item.billingTypeName.includes("(credit)") ? (
                <Tooltip
                  title="Can't issue a credit on credit fee"
                  color="orange"
                >
                  <RefundIcon style={{ fontSize: 16, color: "orange" }} />
                </Tooltip>
              ) : (
                <Tooltip
                  title="Billing already submitted for credit"
                  color="blue"
                >
                  <CreditIcon
                    style={{ fontSize: 16 }}
                    className="icon-primary"
                  />
                </Tooltip>
              )}

              <div className={styles.invoiceDetail}>
                <div className={styles.invoiceDate}>
                  <Title level={5}>
                    Created Date:{" "}
                    <span>
                      {convertIntoDashUSFormat(item.createdAt, false)},
                    </span>
                  </Title>
                  <Title level={5} className={styles.invoiceId}>
                    ID: <span>{item.id}</span>
                  </Title>
                </div>
                <Divider className={styles.invoiceDivider} />
                <div className={styles.invoiceDate}>
                  <Title level={5}>
                    Billing Type: <span>{item.billingTypeName}</span>
                  </Title>
                  <Title level={5}>
                    Amount:{" "}
                    {displayDollar(item.dublicateAmount?.toFixed(2) ?? 0)}
                  </Title>
                </div>
              </div>
            </div>
          ))
        ) : (
          <EmptyPlaceholder />
        )}
      </Scrollbars>
      <SubmitOneTimeJobBilling
        clientId={clientId}
        candidateActivity={candidateActivity}
        isOpen={isModalVisible}
        setIsModalVisible={setIsModalVisible}
        currencyList={configrationSlice.currencies}
        setRefresh={setRefresh}
      />

      <Modal
        centered
        width={520}
        title="Issue a Credit"
        open={issueCreditModal}
        onCancel={handleIssueCreditCancel}
        className="s2-theme-style credit-modal modal-overflow-auto"
        closeIcon={<i className="icon-closeable" />}
        footer={
          <>
            <Button
              className={styles.jobCancelButton}
              onClick={handleIssueCreditCancel}
            >
              Cancel
            </Button>
            <Button
              loading={isLoading}
              onClick={issueCreditForm.submit}
              type="primary"
            >
              Confirm
            </Button>
          </>
        }
      >
        <>
          <Form
            form={issueCreditForm}
            layout="vertical"
            onFinish={issueJobCredit}
          >
            <Form.Item
              name="reason"
              colon
              label="Reason for Credit"
              className={styles.jobFieldLabel}
              rules={[{ required: true, message: "Credit Reason is Required" }]}
            >
              <Input size="medium" placeholder="Enter Reason for Credit" />
            </Form.Item>
            <div className="clientBilling">
              <Title className="credit-text" level={5}>
                Credit Amount:
              </Title>
              <Paragraph>
                &nbsp;
                {displayDollar(
                  `-${issueCreditRef.current?.dublicateAmount ?? 0}`,
                )}
              </Paragraph>
            </div>
          </Form>
        </>
      </Modal>
    </>
  );
}
